import {
  QueryObserverResult,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { dismissRiskInsight, fetchAffectedItems, fetchRiskInsights, updateRiskInsight } from './insights-client';
import type {
  AffectedItemsResponse,
  InsightFilters,
  RiskInsight,
  RiskInsightList,
  RiskInsightUpdate,
} from './insights-client.type';

/**
 * Hook to fetch risk insights with optional filters
 *
 * @param {InsightFilters} filters - Optional filters for insights (type, severity, status, etc.)
 * @param {UseQueryOptions<RiskInsightList[]>} options - Optional query options for configuring the query behavior
 * @returns {{ insights: RiskInsightList[]; query: QueryObserverResult<RiskInsightList[]> }} Object containing insights array and query state
 */
function useRiskInsights(
  filters?: InsightFilters,
  options?: UseQueryOptions<RiskInsightList[]>,
): {
  insights: RiskInsightList[];
  query: QueryObserverResult<RiskInsightList[]>;
} {
  const query = useQuery<RiskInsightList[]>({
    queryKey: ['risk-insights', filters],
    queryFn: () => fetchRiskInsights(filters),
    ...options,
  });

  return {
    insights: query.data || [],
    query,
  };
}

/**
 * Hook to fetch affected items for an insight
 *
 * @param {string} insightId - The ID of the insight
 * @param {UseQueryOptions<AffectedItemsResponse>} options - Optional query options for configuring the query behavior
 * @returns Object containing the affected items and query state
 */
function useAffectedItems(
  insightId: string | undefined,
  options?: UseQueryOptions<AffectedItemsResponse>,
): {
  affectedItems: AffectedItemsResponse | undefined;
  query: QueryObserverResult<AffectedItemsResponse>;
} {
  const query = useQuery<AffectedItemsResponse>({
    queryKey: ['risk-insight-affected-items', insightId],
    queryFn: () => fetchAffectedItems(insightId!),
    enabled: !!insightId,
    ...options,
  });

  return {
    affectedItems: query.data,
    query,
  };
}

/**
 * Hook to update a risk insight
 *
 * @param {UseMutationOptions<RiskInsight, unknown, { insightId: string; data: RiskInsightUpdate }>} options - Optional mutation options for configuring the mutation behavior
 * @returns Mutation object for updating an insight
 */
function useUpdateRiskInsightMutation(
  options?: UseMutationOptions<RiskInsight, unknown, { insightId: string; data: RiskInsightUpdate }>,
): UseMutationResult<RiskInsight, unknown, { insightId: string; data: RiskInsightUpdate }> {
  const queryClient = useQueryClient();

  return useMutation<RiskInsight, unknown, { insightId: string; data: RiskInsightUpdate }>({
    mutationFn: ({ insightId, data }) => updateRiskInsight(insightId, data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['risk-insights'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['risk-insight', variables.insightId], exact: true });
      (options?.onSuccess as any)?.(data, variables, context);
    },
    ...options,
  });
}

/**
 * Hook to dismiss a risk insight
 *
 * @param {UseMutationOptions<RiskInsight, unknown, string>} options - Optional mutation options for configuring the mutation behavior
 * @returns Mutation object for dismissing an insight
 */
function useDismissRiskInsightMutation(
  options?: UseMutationOptions<RiskInsight, unknown, string>,
): UseMutationResult<RiskInsight, unknown, string> {
  const queryClient = useQueryClient();

  return useMutation<RiskInsight, unknown, string>({
    mutationFn: dismissRiskInsight,
    onSuccess: (data, insightId, context) => {
      queryClient.invalidateQueries({ queryKey: ['risk-insights'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['risk-insight', insightId], exact: true });
      (options?.onSuccess as any)?.(data, insightId, context);
    },
    ...options,
  });
}

export { useAffectedItems, useDismissRiskInsightMutation, useRiskInsights, useUpdateRiskInsightMutation };
